import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  signInWithPopup,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  getDocFromServer,
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  addDoc,
  serverTimestamp,
  orderBy,
  limit
} from 'firebase/firestore';
import { auth, db, googleProvider } from './firebasePWA';

console.log('🚀🚀🚀 NOWA WERSJA firebaseAuthPWA.js ZAŁADOWANA!');

// Rejestracja użytkownika
export const registerUser = async (email, password, displayName) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Aktualizuj profil użytkownika
    await updateProfile(user, { displayName });
    
    // Utwórz dokument użytkownika w Firestore
    await setDoc(doc(db, 'users', user.uid), {
      email: user.email,
      displayName: displayName,
      createdAt: serverTimestamp(),
      isPremium: false,
      trialActive: true,
      trialStartDate: serverTimestamp(),
      trialEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
      subscriptionActive: false,
      coins: 100, // Domyślne monety
      lastLogin: serverTimestamp()
    });
    
    return { success: true, user };
  } catch (error) {
    console.error('Błąd rejestracji:', error);
    return { success: false, error: error.message };
  }
};

// Logowanie użytkownika
export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Aktualizuj ostatnie logowanie
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        lastLogin: serverTimestamp()
      });
    } catch (firestoreError) {
      console.warn('⚠️ Błąd aktualizacji Firestore:', firestoreError.message);
      // Kontynuuj mimo błędu Firestore
    }
    
    return { success: true, user };
  } catch (error) {
    console.error('Błąd logowania:', error);
    
    // Obsługa specyficznych błędów Firebase
    if (error.code === 'auth/user-not-found') {
      return { success: false, error: 'Użytkownik nie istnieje' };
    }
    if (error.code === 'auth/wrong-password') {
      return { success: false, error: 'Nieprawidłowe hasło' };
    }
    if (error.code === 'auth/too-many-requests') {
      return { success: false, error: 'Zbyt wiele prób logowania. Spróbuj ponownie później' };
    }
    if (error.code === 'auth/network-request-failed') {
      return { success: false, error: 'Błąd połączenia sieciowego. Sprawdź internet' };
    }
    
    return { success: false, error: error.message };
  }
};

// Logowanie przez Google
export const loginWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    // Sprawdź czy użytkownik już istnieje
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // Utwórz nowego użytkownika
        try {
          await setDoc(doc(db, 'users', user.uid), {
            email: user.email,
            displayName: user.displayName,
            created_at: serverTimestamp(),
            createdAt: serverTimestamp(),
            isPremium: false,
            trialActive: true,
            trial_start_date: new Date().toISOString(),
            trialStartDate: serverTimestamp(),
            trial_end_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
            trialEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
            subscription_status: 'trial',
            subscription_plan: 'free',
            subscriptionActive: false,
            subscription_start_date: null,
            subscription_end_date: null,
            is_blocked: false,
            coins: 100, // Domyślne monety
            lastLogin: serverTimestamp()
          });
        } catch (firestoreError) {
          console.warn('⚠️ Błąd tworzenia użytkownika w Firestore:', firestoreError.message);
          // Kontynuuj mimo błędu Firestore
        }
      } else {
        // Aktualizuj ostatnie logowanie
        try {
          await updateDoc(doc(db, 'users', user.uid), {
            lastLogin: serverTimestamp()
          });
        } catch (firestoreError) {
          console.warn('⚠️ Błąd aktualizacji Firestore:', firestoreError.message);
          // Kontynuuj mimo błędu Firestore
        }
      }
    } catch (firestoreError) {
      console.warn('⚠️ Błąd sprawdzania użytkownika w Firestore:', firestoreError.message);
      // Kontynuuj mimo błędu Firestore
    }
    
    return { success: true, user };
  } catch (error) {
    console.error('Błąd logowania Google:', error);
    
    // Obsługa specyficznych błędów Firebase
    if (error.code === 'auth/popup-blocked') {
      return { success: false, error: 'Popup został zablokowany. Zezwól na popupy dla tej strony.' };
    }
    if (error.code === 'auth/popup-closed-by-user') {
      return { success: false, error: 'Okno logowania zostało zamknięte.' };
    }
    if (error.code === 'auth/network-request-failed') {
      return { success: false, error: 'Błąd połączenia sieciowego. Sprawdź internet.' };
    }
    if (error.code === 'auth/cancelled-popup-request') {
      return { success: false, error: 'Logowanie zostało anulowane.' };
    }
    
    return { success: false, error: error.message };
  }
};

// Wylogowanie użytkownika
export const logoutUser = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Błąd wylogowania:', error);
    return { success: false, error: error.message };
  }
};

// Nasłuchiwanie zmian stanu autoryzacji
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Resetowanie hasła
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error('Błąd resetowania hasła:', error);
    return { success: false, error: error.message };
  }
};

// Aktualizacja dat użytkownika (jeśli brakuje) - NIE NADPISUJE AKTYWNEJ SUBSKRYPCJI!
export const updateUserDates = async (userId) => {
  try {
    console.log('🔍 Aktualizacja dat użytkownika:', userId);
    
    // SPRAWDŹ CZY JUŻ MA AKTYWNĄ SUBSKRYPCJĘ - NIE NADPISUJ!
    const subscriptionDoc = await getDoc(doc(db, 'subscriptions', userId));
    if (subscriptionDoc.exists()) {
      const subData = subscriptionDoc.data();
      const toDate = (value) => {
        if (!value) return null;
        if (typeof value === 'string') return new Date(value);
        if (value instanceof Date) return value;
        if (typeof value.toDate === 'function') return value.toDate();
        return new Date(value);
      };
      const endDate = toDate(subData.subscription_end_date);
      const hasActiveSubscription = subData.subscription_status === 'active' && 
                                    endDate && 
                                    endDate > new Date();
      
      if (hasActiveSubscription) {
        console.log('⚠️ Użytkownik ma aktywną subskrypcję - NIE nadpisuję!');
        // Tylko uzupełnij brakujące pola, nie zmieniaj statusu
        const now = new Date();
        const updateOnlyMissing = {
          updated_at: now.toISOString()
        };
        
        if (!subData.created_at) updateOnlyMissing.created_at = now.toISOString();
        if (!subData.trial_start_date && !subData.subscription_start_date) {
          updateOnlyMissing.trial_start_date = now.toISOString();
        }
        
        await updateDoc(doc(db, 'subscriptions', userId), updateOnlyMissing);
        return { success: true, skipped: true };
      }
    }
    
    const now = new Date();
    const trialEnd = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
    
    const updateData = {
      created_at: now.toISOString(),
      trial_start_date: now.toISOString(),
      trial_end_date: trialEnd.toISOString(),
      subscription_status: 'trial',
      subscription_plan: 'free',
      subscription_start_date: now.toISOString(),
      subscription_end_date: trialEnd.toISOString(),
      is_blocked: false,
      lastLogin: now.toISOString(),
      updated_at: now.toISOString()
    };
    
    await updateDoc(doc(db, 'users', userId), updateData);
    console.log('✅ Daty użytkownika zaktualizowane:', userId);

    const subscriptionData = {
      subscription_status: 'trial',
      subscription_plan: 'free',
      subscription_start_date: now.toISOString(),
      subscription_end_date: trialEnd.toISOString(),
      trial_start_date: now.toISOString(),
      trial_end_date: trialEnd.toISOString(),
      is_blocked: false,
      created_at: now.toISOString(),
      updated_at: now.toISOString()
    };

    await setDoc(doc(db, 'subscriptions', userId), subscriptionData, { merge: true });
    console.log('✅ Dokument subskrypcji ustawiony dla okresu próbnego:', userId);
    return { success: true };
  } catch (error) {
    console.error('❌ Błąd aktualizacji dat użytkownika:', error);
    return { success: false, error: error.message };
  }
};

// Pobieranie statusu użytkownika – TYLKO z subscriptions, NIGDY fallback do users!
export const getUserStatus = async (userId, forceRefresh = false) => {
  try {
    console.log('🔍 getUserStatus - userId:', userId, 'forceRefresh:', forceRefresh);
    // ZAWSZE pobierz subscriptions – nawet jeśli nie istnieje, nigdy users!
    // Używaj poprawnej funkcji getDoc / getDocFromServer zamiast nieobsługiwanej opcji { source: 'server' }
    let subscriptionDoc;
    if (forceRefresh && typeof getDocFromServer === 'function') {
      subscriptionDoc = await getDocFromServer(doc(db, 'subscriptions', userId));
    } else {
      subscriptionDoc = await getDoc(doc(db, 'subscriptions', userId));
    }

    if (subscriptionDoc.exists()) {
      const subData = subscriptionDoc.data();
      const toDate = (value) => {
        if (!value) return null;
        if (typeof value === 'string') return new Date(value);
        if (value instanceof Date) return value;
        if (typeof value.toDate === 'function') return value.toDate();
        return new Date(value);
      };

      const now = new Date();
      const subscriptionEnd = toDate(subData.subscription_end_date);
      const trialEnd = toDate(subData.trial_end_date);
      const rawStatus = subData.subscription_status || 'active';
      const isTrialActive = rawStatus === 'trial' && trialEnd && trialEnd > now;
      const isPaidActive = subscriptionEnd && subscriptionEnd > now;
      const effectiveStatus = isTrialActive ? 'trial' : isPaidActive ? (rawStatus === 'cancelled' ? 'cancelled' : 'active') : rawStatus;
      const isBlocked = !(isTrialActive || isPaidActive);

      return {
        isBlocked,
        subscription: {
          subscription_status: effectiveStatus,
          subscription_plan: isTrialActive ? (subData.subscription_plan || 'free') : (subData.subscription_plan || 'premium'),
          subscription_start_date: subData.subscription_start_date,
          subscription_end_date: subData.subscription_end_date,
          trial_start_date: subData.trial_start_date,
          trial_end_date: subData.trial_end_date,
          is_blocked: isBlocked,
          created_at: subData.created_at,
          updated_at: subData.updated_at
        }
      };
    } else {
      // Jeśli brak dokumentu subscriptions, user jest zablokowany
      console.log('🔍 getUserStatus - brak dokumentu w subscriptions, domyślnie blokada');
      return {
        isBlocked: true,
        subscription: null
      };
    }
  } catch (error) {
    console.error('Błąd sprawdzania statusu użytkownika:', error);
    return { isBlocked: false, subscription: null };
  }
};

// Pobieranie subskrypcji użytkownika – TYLKO z subscriptions, nigdy z users (brak fallback)
export const getUserSubscription = async (userId, forceRefresh = false) => {
  try {
    console.log('🚀🚀🚀 NOWA WERSJA getUserSubscription - START - userId:', userId, 'forceRefresh:', forceRefresh);
    let subscriptionDoc;
    if (forceRefresh && typeof getDocFromServer === 'function') {
      subscriptionDoc = await getDocFromServer(doc(db, 'subscriptions', userId));
    } else {
      subscriptionDoc = await getDoc(doc(db, 'subscriptions', userId));
    }
    if (subscriptionDoc.exists()) {
      const subData = subscriptionDoc.data();
      const convertDate = (dateField) => {
        if (!dateField) return null;
        if (dateField && typeof dateField.toDate === 'function') {
          return dateField.toDate().toISOString();
        }
        if (typeof dateField === 'string') {
          return dateField;
        }
        if (dateField instanceof Date) {
          return dateField.toISOString();
        }
        return null;
      };
      const computedEnd = convertDate(subData.subscription_end_date);
      const trialEnd = convertDate(subData.trial_end_date);
      const now = new Date();
      const rawStatus = subData.subscription_status || 'trial';
      const isTrialActive = rawStatus === 'trial' && trialEnd && new Date(trialEnd) > now;
      let computedStatus = rawStatus;
      if (!isTrialActive && computedEnd) {
        const end = new Date(computedEnd);
        if (end > now) {
          computedStatus = rawStatus === 'cancelled' ? 'cancelled' : 'active';
        } else if (rawStatus === 'cancelled') {
          computedStatus = 'cancelled';
        } else {
          computedStatus = 'expired';
        }
      } else if (isTrialActive) {
        computedStatus = 'trial';
      } else if (!computedEnd) {
        computedStatus = rawStatus;
      }
      return {
        subscription_status: computedStatus,
        subscription_plan: isTrialActive ? (subData.subscription_plan || 'free') : (subData.subscription_plan || 'premium'),
        trial_start_date: convertDate(subData.trial_start_date),
        trial_end_date: convertDate(subData.trial_end_date),
        subscription_start_date: convertDate(subData.subscription_start_date),
        subscription_end_date: computedEnd,
        is_blocked: !(isTrialActive || (computedEnd && new Date(computedEnd) > now)),
      };
    } else {
      console.log('🚫 getUserSubscription - brak subscriptions, domyślnie blokada!');
      return {
        subscription_status: 'expired',
        is_blocked: true
      };
    }
  } catch (error) {
    console.error('Błąd pobierania subskrypcji:', error);
    return {
      subscription_status: 'expired',
      is_blocked: true
    };
  }
};

// Pobieranie historii płatności
export const getPaymentHistory = async (userId) => {
  try {
    const paymentsQuery = query(
      collection(db, 'payments'),
      where('user_id', '==', userId),
      orderBy('created_at', 'desc')
    );
    const snapshot = await getDocs(paymentsQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Błąd pobierania historii płatności:', error);
    // Jeśli błąd indeksu, zwróć pustą tablicę (nie blokuj głównej funkcjonalności)
    if (error.message && error.message.includes('index')) {
      console.warn('⚠️ Błąd indeksu - zwracam pustą historię płatności');
      return [];
    }
    return [];
  }
};

// Sprawdzanie dostępu użytkownika
export const checkUserAccess = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.activeTalisman || null;
    }
    return null;
  } catch (error) {
    console.error('Błąd sprawdzania dostępu:', error);
    return null;
  }
};

// Anulowanie subskrypcji
export const cancelSubscription = async (userId) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      subscriptionActive: false,
      isPremium: false
    });
    return { success: true };
  } catch (error) {
    console.error('Błąd anulowania subskrypcji:', error);
    return { success: false, error: error.message };
  }
};

// Sprawdzanie statusu subskrypcji (dla debugowania)
export const checkSubscriptionStatus = async (userId) => {
  try {
    console.log('🔍 checkSubscriptionStatus - sprawdzam dla userId:', userId);
    
    // Sprawdź subscriptions
    const subscriptionDoc = await getDoc(doc(db, 'subscriptions', userId));
    if (subscriptionDoc.exists()) {
      const subData = subscriptionDoc.data();
      console.log('📋 Dokument subscriptions:', subData);
      
      // Sprawdź daty
      const now = new Date();
      if (subData.subscription_end_date) {
        const endDate = subData.subscription_end_date.toDate ? 
          subData.subscription_end_date.toDate() : 
          new Date(subData.subscription_end_date);
        
        console.log('📅 Data końca subskrypcji:', endDate);
        console.log('📅 Teraz:', now);
        console.log('📅 Czy aktywna:', endDate > now);
      }
      
      return subData;
    } else {
      console.log('❌ Brak dokumentu w subscriptions');
      return null;
    }
  } catch (error) {
    console.error('❌ Błąd checkSubscriptionStatus:', error);
    return null;
  }
};

// Sprawdzanie i blokowanie wygasłych prób
export const checkAndBlockExpiredTrials = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (userData.trialActive && userData.trialEndDate) {
        const trialEnd = userData.trialEndDate.toDate();
        if (new Date() > trialEnd) {
          await updateDoc(doc(db, 'users', userId), {
            trialActive: false
          });
          return false;
        }
      }
      return userData.trialActive;
    }
    return false;
  } catch (error) {
    console.error('Błąd sprawdzania próby:', error);
    return false;
  }
};

